import { cn, LoadingSpinner, Text } from '@youversion/design-system-components';
import {
  CheckmarkStroked,
  InfoCircleStroked,
  LineHoriztonalStroked,
  PauseStroked,
  PlayFilled,
  XStroked,
} from '@youversion/design-system-icons';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ContentStatusProgressBar } from '@/components/cms/ContentStatusProgressBar';
import { UploadFailureMessage } from '@/components/UploadFailureMessage';
import { UPLOAD_STATUS } from '@/constants/upload-workflow';
import { UploadSession } from '@/context/upload-state';
import { useCmsUploadWorkflow } from '@/hooks/use-cms-upload-workflow';
import { useUploadRemoveSession } from '@/store/useUploadStateStore';

interface Props {
  data: UploadSession;
  isLast?: boolean;
  supportUrl: string;
}

export function UploadCard({ data, isLast, supportUrl }: Props) {
  const { t } = useTranslation('common');
  const workflow = useCmsUploadWorkflow(data.operationName);
  const removeSession = useUploadRemoveSession();

  const statusDetails = useMemo(() => {
    if (data.status === UPLOAD_STATUS.completed) {
      return {
        actionIcon: <XStroked className='size-2' />,
        actionLabel: t('common:dismiss'),
        icon: <CheckmarkStroked className='size-3 text-success' />,
        label: t('common:upload_widget.status_labels.completed'),
      };
    }

    if (data.status === UPLOAD_STATUS.failed) {
      return {
        actionIcon: <XStroked className='size-2' />,
        actionLabel: t('common:dismiss'),
        icon: <InfoCircleStroked className='size-3 text-alert' />,
        label: t('common:upload_widget.status_labels.failed'),
      };
    }

    if (data.status === UPLOAD_STATUS.paused) {
      return {
        actionIcon: <PlayFilled className='size-2' />,
        actionLabel: t('common:upload_widget.resume_upload'),
        icon: <LineHoriztonalStroked className='size-3' />,
        label: t('common:upload_widget.status_labels.paused'),
      };
    }

    const actionIcon = workflow.canPause ? <PauseStroked className='size-2' /> : null;
    return {
      actionIcon,
      actionLabel: workflow.canPause ? t('common:upload_widget.pause_upload') : '',
      icon: <LoadingSpinner aria-label={t('common:loading')} height='24px' iconSize={24} width='24px' />,
      label: t('common:upload_widget.status_labels.uploading'),
    };
  }, [data.status, t, workflow.canPause]);

  return (
    <div
      className={cn(
        'pli-2 plb-1.5 border-b-small border-gray-10 dark:border-tertiary-dark flex justify-between items-center gap-x-1',
        {
          'border-b-none': isLast,
        }
      )}
    >
      {statusDetails.icon}
      <div className='w-full space-y-0.5'>
        <Text className='font-medium break-all' size='sm' variant='label'>
          {data.videoUpload.fileMetadata.name}
        </Text>
        {data.status !== UPLOAD_STATUS.failed ? (
          <Text size='sm' variant='paragraph'>
            {statusDetails.label}
          </Text>
        ) : null}
        {data.status === UPLOAD_STATUS.uploading || data.status === UPLOAD_STATUS.paused ? (
          <div className='w-full max-w-[calc(100%-1rem)]'>
            <div className='w-full'>
              <ContentStatusProgressBar.MiniBar
                progress={workflow.uploadProgress}
                status={workflow.progressBarStatus}
              />
            </div>
          </div>
        ) : null}
        {data.status === UPLOAD_STATUS.completed ? (
          <Text className='italic' size='xs' variant='paragraph'>
            {t('common:upload_widget.upload_complete_message')}
          </Text>
        ) : null}
        {data.status === UPLOAD_STATUS.failed ? (
          <UploadFailureMessage
            helperTextClassName='italic'
            kind={data.kind}
            showIcon={false}
            supportUrl={supportUrl}
          />
        ) : null}
      </div>
      {statusDetails.actionIcon ? (
        <button
          aria-label={statusDetails.actionLabel}
          onClick={() => {
            if (data.status === UPLOAD_STATUS.completed || data.status === UPLOAD_STATUS.failed) {
              removeSession(data.operationName);
            } else if (data.status === UPLOAD_STATUS.paused && workflow.canUpload()) {
              workflow.uploadFiles();
            } else if (data.status === UPLOAD_STATUS.uploading && workflow.canPause) {
              workflow.pauseUpload();
            }
          }}
          type='button'
        >
          {statusDetails.actionIcon}
        </button>
      ) : null}
      {!statusDetails.actionIcon &&
      (data.status === UPLOAD_STATUS.uploading || data.status === UPLOAD_STATUS.paused) ? (
        <div aria-hidden={true} className='shrink-0 size-2' />
      ) : null}
    </div>
  );
}
